import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { CustomCargoParametersItemProps } from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/interfaces';
import {
  CustomCargoBaseParameters,
  CustomCargoCalcType,
} from '@pages/AdditionalServices/tabs/DeliveryForm/types';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  checkEmptyInputValue,
  getFormattedFloatNumber,
  getFormattedIntNumber,
  getFormattedNumberForInput,
} from '../../../helpers';
import useDelivery from '../../../useDelivery';

const CustomCargoBase: React.FC<CustomCargoParametersItemProps> = ({
  customCargoParameters,
  onChangeCustomCargoParameters,
  Position,
  isDeletable,
  onDelete,
  onChangeCargoFieldsFilled,
}) => {
  const { t } = useTranslation('Common');

  const { useIsRoutesLoading, useIsLoading } = useDelivery();
  const [isRoutesLoading] = useIsRoutesLoading().useState();
  const [isLoading] = useIsLoading().useState();

  const isHeightInputInvalid = checkEmptyInputValue(customCargoParameters.baseParameters.height);
  const isLengthInputInvalid = checkEmptyInputValue(customCargoParameters.baseParameters.length);
  const isQuantityInputInvalid = checkEmptyInputValue(
    customCargoParameters.baseParameters.quantity
  );
  const isWeightInputInvalid = checkEmptyInputValue(customCargoParameters.baseParameters.weight);
  const isWidthInputInvalid = checkEmptyInputValue(customCargoParameters.baseParameters.width);

  useEffect(() => {
    onChangeCargoFieldsFilled([
      isHeightInputInvalid,
      isLengthInputInvalid,
      isWeightInputInvalid,
      isQuantityInputInvalid,
      isWidthInputInvalid,
    ]);
  }, [
    isHeightInputInvalid,
    isLengthInputInvalid,
    isWeightInputInvalid,
    isQuantityInputInvalid,
    isWidthInputInvalid,
  ]);

  const handleChangeCustomCargoBaseParameters = <T extends keyof CustomCargoBaseParameters>(
    key: T,
    value: CustomCargoBaseParameters[T]
  ) => {
    onChangeCustomCargoParameters({
      ...customCargoParameters,
      baseParameters: {
        ...customCargoParameters.baseParameters,
        [key]: value,
      },
    });
  };

  const handleChangeCustomCargoCalcType = (type: CustomCargoCalcType) => {
    onChangeCustomCargoParameters({
      ...customCargoParameters,
      parametersCargoCalcType: type,
    });
  };

  const handleChangeCustomCargoType = (type: 'base' | 'pallet') => {
    onChangeCustomCargoParameters({
      ...customCargoParameters,
      parametersType: type,
    });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Typography variant={'subtitle2'} color={'#707070'}>
        {t('Common:CargoPlace')} #{Position}
      </Typography>
      <Box className={'cargo-types'}>
        <Typography component={'span'} variant={'h5'} className={'cargo-types__title'}>
          {t('Common:CalculationType')}
        </Typography>
        <Button
          size={'small'}
          variant={'outlined'}
          className={'not-selected'}
          color={'inherit'}
          disabled={isRoutesLoading || isLoading}
          onClick={() => handleChangeCustomCargoCalcType('volume')}
        >
          {t('Common:Consignment')}
        </Button>
        <Button disabled={isRoutesLoading || isLoading} size={'small'} variant={'outlined'}>
          {t('Common:CargoUnit')}
        </Button>
        <Typography component={'span'} variant={'h5'} className={'cargo-types__title'}>
          {t('Common:InputType')}
        </Typography>
        <Button
          size={'small'}
          className={'not-selected'}
          variant={'outlined'}
          color={'inherit'}
          disabled={isRoutesLoading || isLoading}
          onClick={() => handleChangeCustomCargoType('pallet')}
        >
          {t('Common:Pallet')}
        </Button>
        <Button disabled={isRoutesLoading || isLoading} size={'small'} variant={'outlined'}>
          {t('Common:Dimensions')}
        </Button>
      </Box>
      <Box className={'cargo-inputs-block-grid'}>
        <TextField
          error={isWidthInputInvalid}
          focused={isWidthInputInvalid}
          className={'cargo-inputs-block__half-mobile width'}
          variant={'standard'}
          value={customCargoParameters.baseParameters.width}
          disabled={isRoutesLoading || isLoading}
          onFocus={() =>
            handleChangeCustomCargoBaseParameters('width', getFormattedFloatNumber(''))
          }
          onChange={(event) =>
            handleChangeCustomCargoBaseParameters(
              'width',
              getFormattedFloatNumber(event.target.value)
            )
          }
          type={'text'}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:WidthM')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          error={isLengthInputInvalid}
          focused={isLengthInputInvalid}
          className={'cargo-inputs-block__half-mobile length'}
          variant={'standard'}
          value={customCargoParameters.baseParameters.length}
          disabled={isRoutesLoading || isLoading}
          onFocus={() =>
            handleChangeCustomCargoBaseParameters('length', getFormattedFloatNumber(''))
          }
          onChange={(event) =>
            handleChangeCustomCargoBaseParameters(
              'length',
              getFormattedFloatNumber(event.target.value)
            )
          }
          type={'text'}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:LengthM')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          error={isHeightInputInvalid}
          focused={isHeightInputInvalid}
          fullWidth
          variant={'standard'}
          className={'height'}
          value={customCargoParameters.baseParameters.height}
          disabled={isRoutesLoading || isLoading}
          onFocus={() =>
            handleChangeCustomCargoBaseParameters('height', getFormattedFloatNumber(''))
          }
          onChange={(event) =>
            handleChangeCustomCargoBaseParameters(
              'height',
              getFormattedFloatNumber(event.target.value)
            )
          }
          type={'text'}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:HeightM')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          error={isWeightInputInvalid}
          focused={isWeightInputInvalid}
          fullWidth
          variant={'standard'}
          className={'weight'}
          value={customCargoParameters.baseParameters.weight}
          disabled={isRoutesLoading || isLoading}
          onFocus={() =>
            handleChangeCustomCargoBaseParameters('weight', getFormattedFloatNumber(''))
          }
          onChange={(event) => {
            handleChangeCustomCargoBaseParameters(
              'weight',
              getFormattedFloatNumber(event.target.value)
            );
          }}
          type={'text'}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:WeightKg')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          error={isQuantityInputInvalid}
          focused={isQuantityInputInvalid}
          fullWidth
          variant={'standard'}
          className={'seats'}
          value={getFormattedNumberForInput(customCargoParameters.baseParameters.quantity)}
          disabled={isRoutesLoading || isLoading}
          onFocus={() =>
            handleChangeCustomCargoBaseParameters('quantity', getFormattedIntNumber(''))
          }
          onChange={(event) => {
            handleChangeCustomCargoBaseParameters(
              'quantity',
              getFormattedIntNumber(event.target.value)
            );
          }}
          type={'number'}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:SeatsPcs')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {isDeletable && (
        <IconButton
          sx={{
            position: 'absolute',
            right: '-15px',
            top: '-15px',
          }}
          size={'small'}
          disabled={isRoutesLoading || isLoading}
          onClick={() => onDelete()}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default CustomCargoBase;
